// Lara AI · Knowledge Base v1 — plain-text tagged snippets
//
// Matches Niraj's Phase 1 PRD (v0.2 §5): tagged snippets, exact-match
// filtering, no vector search.  Read/write, no LLM anywhere.
//
// Layout:
//   [ Header + KPI strip (total · scope counts · owners) ]
//   [ LEFT rail: scope filter + tag cloud ]
//   [ RIGHT: search + card grid.  Click card → edit drawer. ]
//
// Data-testids: kb-page, kb-scope-{key}, kb-tag-{name}, kb-item-{id},
// kb-search, kb-new, kb-edit-{id}, kb-delete-{id}, kb-drawer, kb-save.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BookOpen, Search, Plus, X, Save, Trash2, Tag, Filter,
  Info, HelpCircle, StickyNote, FileText, Users, Building2, Lightbulb, MessageSquare,
} from "lucide-react";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import LiveCount from "../../components/LiveCount";
import { useProgressiveList } from "../../hooks/useProgressiveList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("talmia_token") || "";
const auth  = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const SCOPE_META = {
  company_context: { icon: Building2,     tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  order_field:     { icon: FileText,      tone: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  customer_field:  { icon: Users,         tone: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10" },
  playbook:        { icon: BookOpen,      tone: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  tip:             { icon: Lightbulb,     tone: "text-orange-300 border-orange-500/40 bg-orange-500/10" },
  question:        { icon: HelpCircle,    tone: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
};

export default function LaraKnowledgeBase() {
  const [items, setItems]         = useState([]);
  const [scopes, setScopes]       = useState([]);
  const [scopeCounts, setScopeCounts] = useState({});
  const [tagCounts, setTagCounts]     = useState({});
  const [total, setTotal]         = useState(0);
  const [filters, setFilters]     = useState({ scope: "", tag: "", q: "" });
  const [loading, setLoading]     = useState(true);
  const [drawerItem, setDrawerItem] = useState(null);

  const loadScopes = async () => {
    try {
      const r = await axios.get(`${API}/kb/scopes`, auth());
      setScopes(r.data.scopes || []);
    } catch (e) { console.error(e); }
  };
  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.scope) params.set("scope", filters.scope);
      if (filters.tag)   params.set("tag",   filters.tag);
      if (filters.q)     params.set("q",     filters.q);
      const r = await axios.get(`${API}/kb/items?${params.toString()}`, auth());
      setItems(r.data.items || []);
      setTotal(r.data.total_active || 0);
      setScopeCounts(r.data.scope_counts || {});
      setTagCounts(r.data.tag_counts || {});
    } catch (e) {
      toast.error("Failed to load KB", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { loadScopes(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.scope, filters.tag]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.q]);

  const topTags = useMemo(() => Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1]).slice(0, 40), [tagCounts]);

  // Progressive reveal over the filtered card list (scope KPIs/tags are global).
  const { visibleCount, showMore } = useProgressiveList(`${filters.scope}|${filters.tag}|${filters.q}`, 50);
  const shownItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="kb-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <BookOpen className="w-5 h-5 text-emerald-300" />
        <h1 className="text-xl font-semibold text-white">Knowledge Base</h1>
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
          Lara AI · v1 · plain-text · agent-ready
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setDrawerItem({ scope: filters.scope || "tip", tags: [], content_md: "", title: "" })}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 text-[11px] font-mono uppercase tracking-widest hover:bg-emerald-500/25"
            data-testid="kb-new">
            <Plus className="w-3.5 h-3.5" /> New entry
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="kb-kpis">
        {scopes.map((s) => {
          const meta = SCOPE_META[s.key] || SCOPE_META.tip;
          const Icon = meta.icon;
          const count = scopeCounts[s.key] || 0;
          const active = filters.scope === s.key;
          return (
            <button key={s.key} onClick={() => setFilters({ ...filters, scope: active ? "" : s.key })}
              data-testid={`kb-scope-${s.key}`}
              className={`rounded-lg border p-3 text-left transition ${
                active ? meta.tone.replace(/text-\S+/, "text-white") : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
              }`}>
              <Icon className={`w-4 h-4 ${meta.tone.split(" ")[0]} mb-1.5`} />
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">{s.label}</div>
              <div className="text-lg font-semibold text-white tabular-nums">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Info banner — only if empty */}
      {total === 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-3 text-[12px] text-amber-200">
          <Info className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Empty knowledge base. Run <code className="font-mono bg-amber-500/10 px-1 rounded">python3 /app/backend/scripts/seed_knowledge_items.py</code> to load the v1 seed.
        </div>
      )}

      {/* Search bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" placeholder="Search title, content, field path, tags…"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full pl-8 pr-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[13px] placeholder-slate-500 focus:border-emerald-500/40 focus:outline-none"
            data-testid="kb-search"
          />
        </div>
        {(filters.scope || filters.tag || filters.q) && (
          <button onClick={() => setFilters({ scope: "", tag: "", q: "" })}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-200 text-[10.5px] font-mono uppercase tracking-widest hover:bg-rose-500/20">
            Clear
          </button>
        )}
        <div className="ml-auto">
          <LiveCount shown={shownItems.length} total={items.length} noun="entry" testId="kb-live-count" />
        </div>
      </div>

      {/* Layout: LEFT rail + RIGHT grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* LEFT rail — tag cloud */}
        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <Tag className="w-3 h-3" /> Tags · exact match
            </div>
            <div className="flex flex-wrap gap-1">
              {topTags.map(([tag, cnt]) => {
                const active = filters.tag === tag;
                return (
                  <button key={tag} onClick={() => setFilters({ ...filters, tag: active ? "" : tag })}
                    data-testid={`kb-tag-${tag.replace(/[^a-z0-9]/gi, "-")}`}
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                      active ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200"
                             : "border-white/10 text-slate-400 hover:bg-white/[0.04]"}`}>
                    #{tag} <span className="text-slate-600">{cnt}</span>
                  </button>
                );
              })}
              {topTags.length === 0 && <span className="text-slate-600 text-[11px] italic">No tags yet</span>}
            </div>
          </div>
          <div className="rounded border border-white/5 bg-white/[0.02] p-3 text-[11px] text-slate-400 leading-relaxed">
            <MessageSquare className="w-3 h-3 inline mr-1 -mt-0.5" />
            KB entries are plain text with tags. When Emergent pros build the agent runtime, they'll query by tag (e.g. <code className="text-emerald-300 font-mono">tags in ["playbook:parked","agent-hint:auto-flag"]</code>) to assemble each agent's context.
          </div>
        </div>

        {/* RIGHT — cards */}
        <div className="space-y-2" data-testid="kb-list">
          {loading && <div className="text-center py-10 text-slate-500 text-[12.5px]">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center py-10 text-slate-500 text-[12.5px] italic">
              No entries match those filters.
            </div>
          )}
          {shownItems.map((it) => <KBCard key={it.id} item={it} onOpen={() => setDrawerItem(it)}
            onDelete={async () => {
              if (!window.confirm(`Delete "${it.title}"?`)) return;
              await axios.delete(`${API}/kb/items/${it.id}`, auth());
              toast.success("Deleted");
              load();
            }}
          />)}
          {!loading && items.length > 0 && (
            <InfiniteSentinel
              onReach={showMore}
              hasMore={shownItems.length < items.length}
              loading={false}
              count={shownItems.length}
              total={items.length}
              testId="kb-sentinel"
            />
          )}
        </div>
      </div>

      {drawerItem && (
        <KBDrawer item={drawerItem} scopes={scopes}
          onClose={() => setDrawerItem(null)}
          onSaved={() => { setDrawerItem(null); load(); }}
        />
      )}
    </div>
  );
}


// ─── Card ──────────────────────────────────────────────────────────────
function KBCard({ item, onOpen, onDelete }) {
  const meta = SCOPE_META[item.scope] || SCOPE_META.tip;
  const Icon = meta.icon;
  return (
    <div className="group rounded-lg border border-white/5 bg-white/[0.02] p-3 hover:bg-white/[0.04] transition"
      data-testid={`kb-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.tone.split(" ")[0]}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <button onClick={onOpen} className="text-[13.5px] font-semibold text-slate-100 hover:text-emerald-200 text-left truncate"
              data-testid={`kb-edit-${item.id}`}>
              {item.title}
            </button>
            <span className={`text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.tone}`}>
              {item.scope.replace("_", " ")}
            </span>
            {item.field_path && (
              <span className="text-[10px] font-mono text-slate-500">
                <code className="text-slate-300">{item.field_path}</code>
              </span>
            )}
            {item.version > 1 && <span className="text-[9.5px] font-mono text-emerald-400">v{item.version}</span>}
          </div>
          <div className="text-[12px] text-slate-400 line-clamp-2 mb-2">
            {stripMarkdown(item.content_md).slice(0, 200)}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {(item.tags || []).slice(0, 6).map((t) => (
              <span key={t} className="text-[9.5px] font-mono text-slate-400 bg-white/[0.03] border border-white/5 px-1.5 py-0.5 rounded">
                #{t}
              </span>
            ))}
            {(item.tags || []).length > 6 && (
              <span className="text-[9.5px] font-mono text-slate-600">+{item.tags.length - 6}</span>
            )}
            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
              <button onClick={onDelete}
                className="p-1 rounded border border-rose-500/30 text-rose-300 hover:bg-rose-500/15"
                title="Delete" data-testid={`kb-delete-${item.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Drawer (create/edit) ──────────────────────────────────────────────
function KBDrawer({ item, scopes, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    title:              item.title || "",
    scope:              item.scope || "tip",
    content_md:         item.content_md || "",
    tags:               (item.tags || []).join(", "),
    field_path:         item.field_path || "",
    evidence_order_ids: (item.evidence_order_ids || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = {
        title:              form.title.trim(),
        scope:              form.scope,
        content_md:         form.content_md,
        tags:               form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        field_path:         form.field_path.trim() || null,
        evidence_order_ids: form.evidence_order_ids.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) await axios.post(`${API}/kb/items`, body, auth());
      else       await axios.patch(`${API}/kb/items/${item.id}`, body, auth());
      toast.success(isNew ? "Created" : "Saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end" data-testid="kb-drawer">
      <div className="w-full max-w-2xl bg-slate-950 border-l border-white/10 overflow-y-auto">
        {/* header */}
        <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-slate-950">
          <BookOpen className="w-4 h-4 text-emerald-300" />
          <div className="text-[13px] text-slate-100 font-semibold">{isNew ? "New KB entry" : `Edit · v${item.version}`}</div>
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => setPreviewMode(!previewMode)}
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-white/10 text-slate-300 hover:bg-white/[0.06]">
              {previewMode ? "Edit" : "Preview"}
            </button>
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-200 text-[10.5px] font-mono uppercase tracking-widest hover:bg-emerald-500/25 disabled:opacity-50"
              data-testid="kb-save">
              <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/[0.06] text-slate-400"
              data-testid="kb-drawer-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* body */}
        <div className="p-4 space-y-4">
          {previewMode ? (
            <>
              <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-1">{form.scope.replace("_", " ")}</div>
                <div className="text-lg font-semibold text-white">{form.title || "(untitled)"}</div>
                {form.field_path && <div className="text-[11px] font-mono text-slate-400 mt-1">📎 {form.field_path}</div>}
              </div>
              <div className="rich-content prose prose-invert prose-sm max-w-none text-[13px] text-slate-200
                [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-white [&_h1]:mb-2 [&_h1]:mt-3
                [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-white [&_h2]:mb-2 [&_h2]:mt-3
                [&_h3]:text-sm  [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-1 [&_h3]:mt-2
                [&_p]:mb-2 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
                [&_code]:text-emerald-300 [&_code]:bg-emerald-500/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:font-mono
                [&_table]:my-2 [&_table]:border-collapse [&_table]:w-full
                [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:text-slate-400 [&_th]:font-mono [&_th]:text-[10px] [&_th]:uppercase [&_th]:tracking-widest [&_th]:border-b [&_th]:border-white/10
                [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-white/5
                [&_strong]:text-white [&_a]:text-emerald-300 [&_a]:underline"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content_md || "*empty*"}</ReactMarkdown>
              </div>
              <div className="flex items-center gap-1 flex-wrap pt-3 border-t border-white/5">
                {form.tags.split(",").map((s) => s.trim()).filter(Boolean).map((t) => (
                  <span key={t} className="text-[9.5px] font-mono text-slate-400 bg-white/[0.03] border border-white/5 px-1.5 py-0.5 rounded">#{t}</span>
                ))}
              </div>
            </>
          ) : (
            <>
              <Field label="Title" required>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[13px] focus:border-emerald-500/40 focus:outline-none"
                  data-testid="kb-form-title" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Scope">
                  <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                    className="w-full px-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[13px] focus:border-emerald-500/40 focus:outline-none"
                    data-testid="kb-form-scope">
                    {scopes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Field path (optional)" hint="e.g. TMOrder.MarginNR">
                  <input value={form.field_path} onChange={(e) => setForm({ ...form, field_path: e.target.value })}
                    className="w-full px-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[12px] font-mono focus:border-emerald-500/40 focus:outline-none"
                    data-testid="kb-form-fieldpath" />
                </Field>
              </div>
              <Field label="Tags" hint="Comma-separated. Use prefixes like `field:`, `domain:`, `playbook:`, `agent-hint:`">
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full px-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[12px] font-mono focus:border-emerald-500/40 focus:outline-none"
                  placeholder="field:MarginNR, domain:margin, agent-hint:auto-flag"
                  data-testid="kb-form-tags" />
              </Field>
              <Field label="Content (Markdown)" hint="Use ## headings, tables, `code`. Rendered as rich text in the Preview tab.">
                <textarea value={form.content_md} onChange={(e) => setForm({ ...form, content_md: e.target.value })}
                  rows={16}
                  className="w-full px-3 py-2 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[12.5px] font-mono focus:border-emerald-500/40 focus:outline-none leading-relaxed"
                  data-testid="kb-form-content" />
              </Field>
              <Field label="Evidence order IDs (optional)" hint="Comma-separated. Records this was learned from — helps future agents cite provenance.">
                <input value={form.evidence_order_ids} onChange={(e) => setForm({ ...form, evidence_order_ids: e.target.value })}
                  className="w-full px-3 py-1.5 rounded border border-white/10 bg-white/[0.02] text-slate-100 text-[12px] font-mono focus:border-emerald-500/40 focus:outline-none"
                  placeholder="BC-ORD-17928, BC-ORD-1024"
                  data-testid="kb-form-evidence" />
              </Field>
              {!isNew && (
                <div className="text-[10.5px] font-mono text-slate-500 pt-3 border-t border-white/5">
                  Owner: <span className="text-slate-300">{item.owner || "—"}</span>  ·  Version: <span className="text-emerald-300">{item.version}</span>  ·  Updated: <span className="text-slate-300">{item.updated_at?.slice(0, 10) || "—"}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[10.5px] font-mono text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1">
        {label} {required && <span className="text-rose-400">*</span>}
        {hint && <span className="text-slate-600 normal-case tracking-normal font-sans">· {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function stripMarkdown(md) {
  return String(md || "")
    .replace(/[#*_`>\[\]|]+/g, " ")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
